<template>
  <div class="p-4">
    <h1 class="text-2xl font-bold mb-4">سجل التغييرات</h1>

    <div class="flex flex-wrap gap-3 mb-4 items-end">
      <div>
        <label class="block mb-1">اسم المستخدم</label>
        <input v-model="username" type="text" class="border px-3 py-2 rounded w-56" placeholder="الكل" />
      </div>
      <div>
        <label class="block mb-1">اسم الجدول</label>
        <input v-model="tableName" type="text" class="border px-3 py-2 rounded w-56" placeholder="الكل" />
      </div>
      <div>
        <label class="block mb-1">الترتيب</label>
        <select v-model="direction" class="border px-3 py-2 rounded">
          <option value="desc">الأحدث أولاً</option>
          <option value="asc">الأقدم أولاً</option>
        </select>
      </div>
      <button @click="search" class="bg-blue-600 text-white px-4 py-2 rounded">بحث</button>
      <button @click="resetFilters" class="bg-gray-200 px-4 py-2 rounded">إزالة الفلاتر</button>
    </div>

    <div v-if="loading" class="py-6">جاري التحميل...</div>
    <div v-else>
      <div v-if="logs.length === 0" class="py-6 text-gray-500">لا توجد بيانات</div>

      <table v-else class="w-full border text-sm">
        <thead class="bg-gray-50">
          <tr>
            <th class="p-2 border">الوقت</th>
            <th class="p-2 border">المستخدم</th>
            <th class="p-2 border">الجدول</th>
            <th class="p-2 border">العملية</th>
            <th class="p-2 border">التفاصيل</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in logs" :key="item.id">
            <td class="p-2 border whitespace-nowrap">{{ formatDate(item.changedAt) }}</td>
            <td class="p-2 border">{{ item.username }}</td>
            <td class="p-2 border">{{ item.tableName }}</td>
            <td class="p-2 border">
              <span :class="badgeClass(item.operation)">{{ item.operation }}</span>
            </td>
            <td class="p-2 border">
              <div class="flex gap-2">
                <button class="text-blue-600" @click="toggle(item)">{{ item._expanded ? 'إخفاء' : 'عرض' }}</button>
              </div>
              <div v-if="item._expanded" class="mt-2 space-y-3">
                <div class="bg-white border p-2 rounded">
                  <div class="font-semibold mb-2">الفروقات</div>
                  <table class="w-full text-xs border">
                    <thead class="bg-gray-50">
                      <tr>
                        <th class="p-1 border text-right">الحقل</th>
                        <th class="p-1 border text-right">قبل</th>
                        <th class="p-1 border text-right">بعد</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="d in diffs(item)" :key="d.key" :class="diffRowClass(d.type)">
                        <td class="p-1 border align-top whitespace-pre-wrap">{{ d.key }}</td>
                        <td class="p-1 border align-top whitespace-pre-wrap">{{ d.beforeDisplay }}</td>
                        <td class="p-1 border align-top whitespace-pre-wrap">{{ d.afterDisplay }}</td>
                      </tr>
                      <tr v-if="diffs(item).length === 0">
                        <td colspan="3" class="p-2 text-gray-500 text-center">لا توجد اختلافات</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div class="bg-red-50 border p-2 rounded">
                    <div class="font-semibold mb-1">قبل</div>
                    <pre class="text-xs overflow-auto" style="max-height: 220px;">{{ pretty(item.beforeData) }}</pre>
                  </div>
                  <div class="bg-green-50 border p-2 rounded">
                    <div class="font-semibold mb-1">بعد</div>
                    <pre class="text-xs overflow-auto" style="max-height: 220px;">{{ pretty(item.afterData) }}</pre>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="flex items-center justify-between mt-4">
        <div class="text-sm text-gray-600">
          صفحة {{ currentPage + 1 }} من {{ totalPages }} — إجمالي {{ totalElements }}
        </div>
        <div class="flex items-center gap-2">
          <button :disabled="currentPage === 0" @click="prev" class="px-3 py-1 border rounded disabled:opacity-50">السابق</button>
          <button :disabled="currentPage >= totalPages - 1" @click="next" class="px-3 py-1 border rounded disabled:opacity-50">التالي</button>
          <select v-model.number="pageSize" @change="reload" class="border px-2 py-1 rounded">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
          </select>
        </div>
      </div>
    </div>
  </div>
  
</template>

<script>
import axios from 'axios';
import url from '../url';

export default {
  name: 'AuditLogs',
  data() {
    return {
      logs: [],
      loading: false,
      username: '',
      tableName: '',
      direction: 'desc',
      currentPage: 0,
      pageSize: 20,
      totalPages: 0,
      totalElements: 0,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize,
          direction: this.direction,
          username: this.username?.trim() || undefined,
          tableName: this.tableName?.trim() || undefined,
        };
        const fullUrl = process.env.VUE_APP_SERVICE_URL + url.audit_logs;
        const res = await axios.get(fullUrl, {
          params,
          headers: {
            'X-Authorization': localStorage.getItem('token')
          }
        });
        const data = res.data?.result?.data || res.data?.data;
        this.logs = (data?.content || []).map(x => ({ ...x, _expanded: false }));
        this.totalElements = data?.totalElements || 0;
        this.totalPages = data?.totalPages || 0;
        this.currentPage = data?.currentPage || 0;
        this.pageSize = data?.pageSize || this.pageSize;
      } catch (e) {
        console.error('Failed loading audit logs', e);
        this.logs = [];
      } finally {
        this.loading = false;
      }
    },
    search() {
      this.currentPage = 0;
      this.load();
    },
    resetFilters() {
      this.username = '';
      this.tableName = '';
      this.direction = 'desc';
      this.currentPage = 0;
      this.load();
    },
    prev() {
      if (this.currentPage > 0) {
        this.currentPage--;
        this.load();
      }
    },
    next() {
      if (this.currentPage < this.totalPages - 1) {
        this.currentPage++;
        this.load();
      }
    },
    reload() {
      this.currentPage = 0;
      this.load();
    },
    toggle(item) {
      item._expanded = !item._expanded;
    },
    pretty(json) {
      if (!json) return '—';
      try {
        const obj = typeof json === 'string' ? JSON.parse(json) : json;
        return JSON.stringify(obj, null, 2);
      } catch {
        return String(json);
      }
    },
    diffs(item) {
      const before = this.parseJsonSafe(item.beforeData);
      const after = this.parseJsonSafe(item.afterData);
      const flatBefore = this.flattenObject(before);
      const flatAfter = this.flattenObject(after);
      const keys = new Set([
        ...Object.keys(flatBefore),
        ...Object.keys(flatAfter)
      ]);
      const result = [];
      keys.forEach(k => {
        const b = Object.prototype.hasOwnProperty.call(flatBefore, k) ? flatBefore[k] : undefined;
        const a = Object.prototype.hasOwnProperty.call(flatAfter, k) ? flatAfter[k] : undefined;
        if (b === undefined && a !== undefined) {
          result.push({ key: k, type: 'added', beforeDisplay: '—', afterDisplay: this.valueDisplay(a) });
        } else if (b !== undefined && a === undefined) {
          result.push({ key: k, type: 'removed', beforeDisplay: this.valueDisplay(b), afterDisplay: '—' });
        } else if (!this.isEqualScalar(b, a)) {
          result.push({ key: k, type: 'modified', beforeDisplay: this.valueDisplay(b), afterDisplay: this.valueDisplay(a) });
        }
      });
      return result;
    },
    parseJsonSafe(val) {
      if (!val) return null;
      try {
        return typeof val === 'string' ? JSON.parse(val) : val;
      } catch {
        return val;
      }
    },
    flattenObject(obj, prefix = '', out = {}) {
      if (obj === null || obj === undefined) return out;
      if (typeof obj !== 'object') {
        out[prefix || 'value'] = obj;
        return out;
      }
      if (Array.isArray(obj)) {
        obj.forEach((v, i) => {
          const p = prefix ? prefix + '[' + i + ']' : '[' + i + ']';
          if (typeof v === 'object' && v !== null) {
            this.flattenObject(v, p, out);
          } else {
            out[p] = v;
          }
        });
        return out;
      }
      Object.keys(obj).forEach(k => {
        const v = obj[k];
        const p = prefix ? prefix + '.' + k : k;
        if (typeof v === 'object' && v !== null) {
          this.flattenObject(v, p, out);
        } else {
          out[p] = v;
        }
      });
      return out;
    },
    isEqualScalar(a, b) {
      if (a === b) return true;
      // Compare date-like values by timestamp
      if (this.isDateLike(a) && this.isDateLike(b)) {
        return new Date(a).getTime() === new Date(b).getTime();
      }
      // Deep-compare plain objects
      if (typeof a === 'object' && typeof b === 'object') {
        try {
          return JSON.stringify(a) === JSON.stringify(b);
        } catch (e) {
          return false;
        }
      }
      return false;
    },
    isDateLike(x) {
      if (x === null || x === undefined) return false;
      const t = Date.parse(x);
      return !Number.isNaN(t);
    },
    valueDisplay(v) {
      if (v === null || v === undefined) return 'null';
      if (typeof v === 'object') {
        try { return JSON.stringify(v); } catch { return String(v); }
      }
      return String(v);
    },
    diffRowClass(type) {
      if (type === 'added') return 'bg-green-50';
      if (type === 'removed') return 'bg-red-50';
      if (type === 'modified') return 'bg-yellow-50';
      return '';
    },
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleString('ar-SA');
      } catch {
        return iso;
      }
    },
    badgeClass(op) {
      const base = 'px-2 py-1 rounded text-white text-xs';
      if (op === 'INSERT') return base + ' bg-green-600';
      if (op === 'UPDATE') return base + ' bg-yellow-600';
      if (op === 'DELETE') return base + ' bg-red-600';
      return base + ' bg-gray-600';
    }
  }
}
</script>

<style scoped>
</style>


